import React from 'react';
import {NavLink, useNavigate} from 'react-router-dom';

import CreateIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Sync';
import {
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {apiCall, getRole} from '../../utils';

import {TranslatorAccessesDTO} from '@bumpy/i18n_common';

type EditItem = {
  key: string;
  email: string;
  projectUuid: string;
  revisionUuid: string;
  langs: string;
};

export const TranslatorsAccessesList: React.FC = () => {
  const navigate = useNavigate();
  const [dto, setDto] = React.useState<TranslatorAccessesDTO>({
    accesses: {},
    projects: {},
  });
  const [editItem, setEditItem] = React.useState<EditItem>();
  const originalDto = React.useRef<TranslatorAccessesDTO>();

  React.useEffect(() => {
    if (getRole() !== 'admin') {
      navigate('/');
      return;
    }
  }, []);

  const refresh = () => {
    apiCall({
      method: 'GET',
      url: '/translators',
      secure: true,
    }).then((response) => {
      setDto(response.data);
      originalDto.current = JSON.parse(JSON.stringify(response.data));
    });
  };

  React.useEffect(refresh, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <Breadcrumbs aria-label="breadcrumb">
          <NavLink to="/">Projects</NavLink>
          <Typography color="secondary">Translator accesses</Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={5}>
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              const newDto = {...dto};
              if (!newDto.accesses['']) {
                newDto.accesses[''] = [];
              }
              const projectUuid = Object.keys(dto.projects)[0];
              const revisionUuid = Object.keys(
                dto.projects[projectUuid].revisions,
              )[0];
              newDto.accesses[''].push({
                projectUuid,
                revisionUuid,
                langs: [],
              });
              setDto(newDto);
              setEditItem({
                key: `_${projectUuid}_${revisionUuid}`,
                email: '',
                projectUuid,
                revisionUuid,
                langs: 'es',
              });
            }}
            startIcon={<CreateIcon />}
          >
            New access
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={refresh}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            disabled={
              JSON.stringify(originalDto.current) === JSON.stringify(dto)
            }
            color="primary"
            onClick={() => {
              apiCall({
                method: 'POST',
                data: dto.accesses,
                url: '/translators',
                secure: true,
              }).then(() => {
                setDto({...dto});
                originalDto.current = JSON.parse(JSON.stringify(dto));
              });
            }}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Revision</TableCell>
                <TableCell>Langs</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(dto.accesses).map((email) => {
                const accesses = dto.accesses[email];
                return accesses.map((access, index) => {
                  const project = dto.projects[access.projectUuid];
                  const key = `${email}_${access.projectUuid}_${access.revisionUuid}`;
                  if (editItem && key === editItem.key) {
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={editItem.email}
                            size="small"
                            label="Email"
                            margin="none"
                            onChange={(event) => {
                              setEditItem({
                                ...editItem,
                                email: event.target.value,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            fullWidth
                            value={editItem.projectUuid}
                            variant="standard"
                            onChange={(event) => {
                              const projectUuid = event.target.value;
                              setEditItem({
                                ...editItem,
                                projectUuid,
                                revisionUuid: Object.keys(
                                  dto.projects[projectUuid].revisions,
                                )[0],
                              });
                            }}
                          >
                            {Object.keys(dto.projects).map((uuid) => {
                              const project = dto.projects[uuid];
                              return (
                                <MenuItem key={uuid} value={uuid}>
                                  {project.name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            fullWidth
                            value={editItem.revisionUuid}
                            variant="standard"
                            onChange={(event) => {
                              setEditItem({
                                ...editItem,
                                revisionUuid: event.target.value,
                              });
                            }}
                          >
                            {Object.keys(
                              dto.projects[editItem.projectUuid].revisions,
                            ).map((uuid) => {
                              return (
                                <MenuItem key={uuid} value={uuid}>
                                  {
                                    dto.projects[editItem.projectUuid]
                                      .revisions[uuid]
                                  }
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={editItem.langs}
                            size="small"
                            label="Langs (comma separated)"
                            margin="none"
                            onChange={(event) => {
                              setEditItem({
                                ...editItem,
                                langs: event.target.value,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => {
                              const newDto = {...dto};
                              for (const email of Object.keys(
                                newDto.accesses,
                              )) {
                                const accesses = newDto.accesses[email];
                                for (const access of accesses) {
                                  if (
                                    editItem.key ===
                                    `${email}_${access.projectUuid}_${access.revisionUuid}`
                                  ) {
                                    access.projectUuid = editItem.projectUuid;
                                    access.revisionUuid = editItem.revisionUuid;
                                    access.langs = editItem.langs.split(',');
                                    if (email !== editItem.email) {
                                      if (newDto.accesses[email].length === 1) {
                                        delete newDto.accesses[email];
                                      }
                                      let existingAccesses =
                                        newDto.accesses[editItem.email];
                                      if (!existingAccesses) {
                                        existingAccesses = [];
                                        newDto.accesses[editItem.email] =
                                          existingAccesses;
                                      }
                                      existingAccesses.push(access);
                                    }
                                    setDto(newDto);
                                    setEditItem(undefined);
                                    return;
                                  }
                                }
                              }
                            }}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton>
                            <IconButton
                              onClick={() => {
                                setEditItem(undefined);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  } else {
                    return (
                      <TableRow key={key}>
                        <TableCell>{email}</TableCell>
                        <TableCell>{project.name}</TableCell>
                        <TableCell>
                          {project.revisions[access.revisionUuid]}
                        </TableCell>
                        <TableCell>{access.langs.join(',')}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => {
                              setEditItem({
                                key,
                                email,
                                projectUuid: access.projectUuid,
                                revisionUuid: access.revisionUuid,
                                langs: access.langs.join(','),
                              });
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              accesses.splice(index, 1);
                              if (accesses.length === 0) {
                                delete dto.accesses[email];
                              }
                              setDto({...dto});
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};
